// getFlattenedKeys is defined inline below

export function getFlattenedKeys(obj: any, prefix = '', limit = 15): string[] {
    if (!obj || typeof obj !== 'object') return [];

    let keys: string[] = [];
    
    // If root is an array, slice it to the limit
    let keysToProcess = Object.keys(obj);
    if (Array.isArray(obj)) {
        keysToProcess = keysToProcess.slice(0, limit);
    }

    keysToProcess.forEach((key) => {
        const val = obj[key];
        const newPrefix = prefix ? `${prefix}.${key}` : key;

        if (val !== null && typeof val === 'object') {
            if (Array.isArray(val)) {
                // Flatten up to `limit` items in the array
                const itemsToFlatten = val.slice(0, limit);
                itemsToFlatten.forEach((item, index) => {
                    const arrayPrefix = `${newPrefix}[${index}]`;
                    if (typeof item === 'object' && item !== null) {
                        keys = keys.concat(getFlattenedKeys(item, arrayPrefix, limit));
                    } else {
                        keys.push(arrayPrefix);
                    }
                });
            } else {
                keys = keys.concat(getFlattenedKeys(val, newPrefix, limit));
            }
        } else {
            keys.push(newPrefix);
        }
    });
    return keys;
}

const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export function findFuzzyMatch(
    layerName: string,
    flattenedKeys: string[],
    category: string
): string | null {
    // Extract any number from the layer name, e.g. "Title 2" -> index = 1
    // Matches standalone numbers (e.g. "Title 2") or trailing digits (e.g. "Title2")
    const numberMatch = layerName.match(/\b(\d+)\b/) || layerName.match(/(\d+)/);
    const targetIdx = numberMatch ? parseInt(numberMatch[1]) - 1 : 0; // default to 0 (Item 1)

    // Clean layerName by removing the number so it matches keywords (e.g. "Title 2" -> "title")
    const cleanedName = clean(layerName.replace(/\d+/g, ''));
    if (!cleanedName) return null;

    // Filter flattenedKeys to only match the target index
    const relevantKeys = flattenedKeys.filter(key => {
        // If targetIdx is 0, match keys that have [0], 0., or no index at all (single objects)
        if (targetIdx === 0) {
            const hasIndexPattern = /\[\d+\]/.test(key) || /^\d+\./.test(key);
            if (!hasIndexPattern) return true;
            return key.toLowerCase().includes('[0]') || key.toLowerCase().startsWith('0.');
        } else {
            // For targetIdx > 0, we strictly require the corresponding index in the key path
            return key.toLowerCase().includes(`[${targetIdx}]`) || key.toLowerCase().startsWith(`${targetIdx}.`);
        }
    });

    // Dictionary mappings (Layer keywords -> API field path keywords)
    const mappings: Record<string, Record<string, string[]>> = {
        weather: {
            temp: ['temp', 'temperature', 'temp_c', 'temp_f', 'degree', 'celsius', 'main.temp', 'current.temp_c'],
            humidity: ['humidity', 'hum', 'current.humidity', 'main.humidity'],
            wind: ['wind', 'speed', 'windspeed', 'wind_speed', 'current.wind_kph', 'wind.speed'],
            condition: ['condition', 'desc', 'description', 'weather', 'text', 'condition.text', 'weather[0].description'],
            icon: ['icon', 'condition.icon', 'weather[0].icon'],
            city: ['name', 'city', 'location.name', 'cityName']
        },
        prayer: {
            fajr: ['fajr', 'Fajr', 'timings.Fajr', 'data.timings.Fajr'],
            dhuhr: ['dhuhr', 'Dhuhr', 'zuhr', 'Zuhr', 'timings.Dhuhr', 'data.timings.Dhuhr'],
            asr: ['asr', 'Asr', 'timings.Asr', 'data.timings.Asr'],
            maghrib: ['maghrib', 'Maghrib', 'timings.Maghrib', 'data.timings.Maghrib'],
            isha: ['isha', 'Isha', 'isya', 'timings.Isha', 'data.timings.Isha'],
            sunrise: ['sunrise', 'Sunrise', 'timings.Sunrise', 'data.timings.Sunrise'],
            imsak: ['imsak', 'Imsak', 'timings.Imsak', 'data.timings.Imsak']
        },
        forex: {
            usd: ['usd', 'USD', 'rates.USD', 'US_Dollar'],
            eur: ['eur', 'EUR', 'rates.EUR', 'Euro'],
            gbp: ['gbp', 'GBP', 'rates.GBP', 'Pound'],
            sar: ['sar', 'SAR', 'rates.SAR', 'Riyal'],
            aed: ['aed', 'AED', 'rates.AED', 'Dirham']
        }
    };

    const categoryMap = mappings[category.toLowerCase()];
    
    // 1. Check dictionary first (for category-specific rules)
    if (categoryMap) {
        for (const [key, searchKeys] of Object.entries(categoryMap)) {
            if (cleanedName.includes(key)) {
                for (const searchKey of searchKeys) {
                    const match = relevantKeys.find(
                        (k) =>
                            clean(k) === clean(searchKey) ||
                            clean(k).endsWith(clean(searchKey))
                    );
                    if (match) return match;
                }
            }
        }
    }

    // 2. Fallback to exact or direct contains check
    const exactMatch = relevantKeys.find((k) => clean(k) === cleanedName);
    if (exactMatch) return exactMatch;

    const partialMatch = relevantKeys.find(
        (k) =>
            clean(k).includes(cleanedName) ||
            cleanedName.includes(clean(k.split('.').pop() || ''))
    );
    if (partialMatch) return partialMatch;

    return null;
}

export function autoMapTemplate(
    layers: any[],
    textStyles: Record<string, any> = {},
    imageSettings: Record<string, any> = {},
    scrollerSettings: Record<string, any> = {},
    customApi: any
) {
    if (!customApi || !customApi.customapidata) {
        return { textStyles, imageSettings, scrollerSettings, count: 0 };
    }

    const flattenedKeys = getFlattenedKeys(customApi.customapidata);
    const apiId = String(customApi._id);
    const category = customApi.api_type || 'general';

    const newTextStyles = { ...textStyles };
    const newImageSettings = { ...imageSettings };
    const newScrollerSettings = { ...scrollerSettings };
    let count = 0;

    // Recurse helper to traverse the design layer tree
    const traverse = (list: any[]) => {
        list.forEach((layer) => {
            if (layer.children && Array.isArray(layer.children)) {
                traverse(layer.children);
            }

            const layerName = layer.name || '';
            const match = findFuzzyMatch(layerName, flattenedKeys, category);

            if (match) {
                const binding = {
                    customApiId: apiId,
                    apiKey: match,
                    fallbackValue: layer.content || '',
                };

                if (layer.type === 'text') {
                    newTextStyles[layer.id] = {
                        ...(newTextStyles[layer.id] || {}),
                        binding,
                    };
                    count++;
                } else if (layer.type === 'image') {
                    newImageSettings[layer.id] = {
                        ...(newImageSettings[layer.id] || {}),
                        binding,
                    };
                    count++;
                } else if (layer.type === 'scroller') {
                    newScrollerSettings[layer.id] = {
                        ...(newScrollerSettings[layer.id] || {}),
                        binding,
                    };
                    count++;
                }
            }
        });
    };

    traverse(layers);

    return {
        textStyles: newTextStyles,
        imageSettings: newImageSettings,
        scrollerSettings: newScrollerSettings,
        count,
    };
}
